// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  PointElement,
  LineController,
  LineElement,
  TimeScale,
  ScatterDataPoint,
  Tooltip,
  TooltipItem,
  FontSpec,
} from "chart.js";
import "chartjs-adapter-luxon"; // needed to support chartjs time series graphs
import annotationPlugin, { AnnotationOptions } from "chartjs-plugin-annotation";
import { Line } from "react-chartjs-2";
import ResizeObserver from "resize-observer-polyfill";

import { IAction } from "actions";
import { isSet, roundToMinDigits } from "utils";
import { hexToRgba } from "guiutils";
import { ConnectorStatus, getColor } from "model/connector";
import { IState } from "states/states";
import {
  IChargerStateEvent,
  ITrendData,
  ITrendDataRow,
  ITrendDataTimestamp,
} from "states/trendingstates";
import { IOcppChargingSession, ITariff } from "states/ocppstates";

import cssContent from "content.scss";

// register used chartjs elements
ChartJS.register(LineController, LineElement, PointElement, TimeScale, Tooltip, annotationPlugin);

interface IChargingSessionGraphProps {
  isLoading: boolean;
  connector: number;
  session: IOcppChargingSession | undefined;
  chargingStates: IChargerStateEvent[] | undefined;
  trendData: ITrendData | undefined;
  showTargetValue: boolean;
  showChargerTariff: boolean;
  showSoc: boolean;
  tariffList: ITariff[] | undefined;
  targetTimestamp: number | undefined;
}

const mapStateToProps = (state: IState) => {
  let session: IOcppChargingSession | undefined;
  if (isSet(state.router.transaction)) {
    session = state.ocpp.chargingSession.find((curItem) => curItem.id === state.router.transaction);
  } else {
    // keep consistent with session loading in sagas:handleNavigation
    session = state.ocpp.pendingChargingSession[0];
  }
  const chargerId = session?.fk_charger;
  const user = state.site.userList?.find((curUser) => curUser.secId === state.site.user?.secId);
  const trendData = state.trending.chargerTrend?.[chargerId!];
  // show soc if any trend data item contains soc data
  const showSoc =
    isSet(trendData) &&
    isSet(session?.connector) &&
    (Object.values(trendData) as ITrendDataTimestamp[]).some((curItem) =>
      isSet(curItem[session!.connector]?.soc),
    );

  const props: IChargingSessionGraphProps = {
    isLoading:
      isSet(state.scheduler.chargingSession.lastRequest) &&
      state.scheduler.chargingSession.lastRequest > (state.scheduler.chargingSession.lastData ?? 0),
    connector: session?.connector ?? 1,
    session,
    chargingStates: state.trending.chargerStateData,
    trendData,
    showTargetValue: false, // do not show limit is it does not work reliable and also changes scale
    showChargerTariff: user?.settings?.showChargerTariff === true,
    showSoc,
    tariffList: state.ocpp.sessionTariff,
    targetTimestamp: state.ocpp.chargingControl?.targetTimestamp ?? undefined,
  };
  return props;
};

interface IChargingSessionGraphDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargingSessionGraphDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({
  dataPower: {
    id: "charging power",
  },
  dataCosts: {
    id: "tariff",
  },
  dataSoc: {
    id: "SoC",
  },
  graphFormatAxisDay: {
    id: "MMM d",
  },
  graphFormatAxisHour: {
    id: "h a",
  },
  graphFormatAxisMinute: {
    id: "h:mm a",
  },
});

class ChargingSessionGraphComponent extends React.Component<
  IChargingSessionGraphProps & IChargingSessionGraphDispatch & WrappedComponentProps
> {
  private chartRef = React.createRef<Chart>();
  private readonly compRef = React.createRef<HTMLDivElement>();
  private resizeObserver: ResizeObserver | undefined;
  private oldHeight = 0;
  private height = 0;

  private startTimestamp: number | undefined;
  private stopTimestamp: number | undefined;
  private values: ScatterDataPoint[] = [];
  private limits: ScatterDataPoint[] = [];
  private socs: ScatterDataPoint[] = [];
  private costs: ScatterDataPoint[] = [];
  private tariffs: ITariff[] = [];

  componentDidMount() {
    this.resizeObserver = new ResizeObserver((elem: ResizeObserverEntry[]) => {
      if (this.chartRef.current && elem[0].contentRect.height) {
        this.oldHeight = this.height;
        this.height = elem[0].contentRect.height;
        //console.log("forcing update", this.oldHeight, this.height);
      }
    });
    if (this.compRef.current) this.resizeObserver.observe(this.compRef.current);
    else console.warn("did not register graph size observer");
  }

  componentWillUnmount() {
    this.resizeObserver?.disconnect();
  }

  private preProcessData() {
    // determine start timestamp of graph
    this.startTimestamp = Math.min(
      this.props.chargingStates?.[0]?.timestamp ?? Date.now() / 1000,
      this.props.session?.startTimestamp ?? Date.now() / 1000,
    );
    // stop timestamp depends on whether the charging is still ongoing
    this.stopTimestamp = this.props.session?.stopTimestamp
      ? Math.max(
          this.props.session.stopTimestamp,
          this.props.chargingStates?.[this.props.chargingStates.length - 1]?.timestamp ?? 0,
        )
      : Math.max(this.props.targetTimestamp ?? 0, Date.now() / 1000);

    // build data arrays
    this.values = []; // clear values from former run
    this.limits = []; // clear values from former run
    this.socs = []; // clear values from former run
    this.costs = [];
    let lastLimit: number | undefined;
    let lastPowerTimestamp: number | undefined;
    let lastStateEventTimestamp: number | undefined;
    let nextStateIndex = 0;
    const chargingStates = this.getStateEvents();
    Object.keys(this.props.trendData ?? []).forEach((curTimestamp) => {
      const timestamp = parseInt(curTimestamp); // just for type matching
      // skip timestamps before start of session
      if (this.startTimestamp && timestamp < this.startTimestamp) {
        // console.warn("skipping outside", this.startTimestamp, timestamp);
        return;
      }

      // check whether we missed a state change event
      while (chargingStates[nextStateIndex]?.timestamp < timestamp) {
        // yes, state change occured
        if (
          [ConnectorStatus.SUSPENDED_EV, ConnectorStatus.SUSPENDED_EVSE].includes(
            chargingStates[nextStateIndex].statusCode,
          ) &&
          this.startTimestamp &&
          chargingStates[nextStateIndex]?.timestamp > this.startTimestamp
        ) {
          // state suspended entered; add a zero data value
          lastStateEventTimestamp = chargingStates[nextStateIndex].timestamp;
          this.values.push({ x: lastStateEventTimestamp * 1000, y: 0 });
          // console.log("push state event", chargingStates[nextStateIndex]);
        }
        nextStateIndex += 1;
      }

      const curTimeData = this.props.trendData![timestamp];
      const curDataRow = curTimeData[this.props.connector] as ITrendDataRow | undefined;
      if (!curDataRow) {
        // skip this entry, it might be a total power of the station itself (connector = 0)
        // console.warn("no connector data", timestamp, this.props.connector, curTimeData);
        return;
      }
      if (curDataRow) {
        const regPeriod = curDataRow.regperiod ?? 0;
        let startTimestamp = timestamp - regPeriod;
        // limit startTimestamp to last power value
        if (lastPowerTimestamp && startTimestamp < lastPowerTimestamp) {
          // this happens regulary with Keba chargers, probably because of
          //console.warn("unordered charging data", curDataRow, lastPowerTimestamp);
          return;
        }
        // limit startTimestamp to last state event
        if (lastStateEventTimestamp && lastStateEventTimestamp > startTimestamp) {
          startTimestamp = lastStateEventTimestamp;
          //console.log("shift to state event", lastStateEventTimestamp);
        }
        if (isSet(curDataRow.targetpower)) {
          lastLimit = curDataRow.targetpower;
        }
        // direct power value
        if (isSet(curDataRow.power)) {
          //if (power === undefined) console.log("using em", powerEm);
          const value = curDataRow.power;
          // very first entry, already > 0?
          if (lastPowerTimestamp === undefined && value && value > 0) {
            // add starting zero
            this.values.push({ x: startTimestamp * 1000, y: 0 });
          }
          this.values.push({ x: startTimestamp * 1000, y: value! });
          lastPowerTimestamp = timestamp;
          this.limits.push({ x: startTimestamp * 1000, y: lastLimit! }); // lastLimit might be undefined
        }

        // soc trending
        if (isSet(curDataRow.soc)) {
          this.socs.push({ x: startTimestamp * 1000, y: curDataRow.soc });
        }
      }
    });

    // add trailing zero value?
    if (
      this.props.session?.stopTimestamp &&
      lastPowerTimestamp &&
      this.values.length &&
      this.values[this.values.length - 1].y
    ) {
      this.values.push({ x: lastPowerTimestamp * 1000, y: 0 });
      this.limits.push({ x: lastPowerTimestamp * 1000, y: lastLimit! });
    }
    // sorting is not needed if we skip older events, but keep it for safety
    this.values = this.values.sort((a, b) => a.x - b.x);
    //console.log("values", this.values, this.props.trendData);

    // tariff trending
    this.addCostData();
  }

  private addCostData() {
    this.tariffs =
      this.props.tariffList
        ?.filter(
          (curItem) =>
            curItem.id === this.props.session?.fk_tariff &&
            (!isSet(curItem.validUntil) || curItem.validUntil > this.startTimestamp!) &&
            (!isSet(curItem.validFrom) || curItem.validFrom < this.stopTimestamp!),
        )
        .sort((a, b) => (a.validFrom ?? 0) - (b.validFrom ?? 0)) ?? [];
    // add start timestamp
    let lastCost: number | undefined;
    this.tariffs.forEach((curItem) => {
      if (
        !isSet(lastCost) &&
        (!isSet(curItem.validUntil) || curItem.validUntil > this.startTimestamp!)
      ) {
        // very first item
        lastCost = curItem.costPerEnergy!;
        this.costs.push({
          x: this.startTimestamp! * 1000,
          y: lastCost,
        });
      }
      if (isSet(lastCost) && isSet(curItem.validFrom) && curItem.validFrom < this.stopTimestamp!) {
        lastCost = curItem.costPerEnergy!;
        this.costs.push({
          x: curItem.validFrom * 1000,
          y: lastCost,
        });
      }
    });
    // add very last item
    if (isSet(lastCost) && isSet(this.stopTimestamp)) {
      this.costs.push({
        x: this.stopTimestamp * 1000,
        y: lastCost,
      });
    }
  }

  private getStateEvents() {
    // show messages from station and connector; hide Etrel socket reconnect messages
    return (
      this.props.chargingStates
        ?.filter(
          (curItem) =>
            (curItem.connector === this.props.connector || curItem.connector === 0) &&
            curItem.info !== "ConnectionWithCentralSystemRestored",
        )
        // ensure sorting by timestamp, which might not be the case if we got live data
        .sort((a, b) => a.timestamp - b.timestamp) ?? []
    );
  }

  private createGraphData() {
    // setup graph data structure
    const data: ChartData<"line", ScatterDataPoint[], unknown> = {
      datasets: [
        {
          label: this.props.intl.formatMessage(messages.dataPower),
          data: this.values,
        },
      ],
    };
    if (this.props.showTargetValue) {
      data.datasets.push({
        label: this.props.intl.formatMessage(messages.dataPower),
        data: this.limits,
        borderColor: cssContent.GRAPH_COLOR_TARGET,
        backgroundColor: cssContent.GRAPH_COLOR_TARGET,
        fill: false,
      });
    }
    if (this.props.showChargerTariff) {
      data.datasets.push({
        label: this.props.intl.formatMessage(messages.dataCosts),
        data: this.costs,
        yAxisID: "yCost",
        borderColor: cssContent.GRAPH_COLOR_ANALOG,
        backgroundColor: "white", // cssContent.GRAPH_COLOR_ANALOG,
        borderWidth: 2, // default: 3
        fill: false,
      });
    }
    if (this.props.showSoc) {
      data.datasets.push({
        label: this.props.intl.formatMessage(messages.dataSoc),
        data: this.socs,
        yAxisID: "ySoc",
        borderColor: cssContent.GRAPH_COLOR_TREND,
        backgroundColor: "white", // cssContent.GRAPH_COLOR_ANALOG,
        borderWidth: 2, // default: 3
        fill: false,
      });
    }
    return data;
  }

  private createGraphOptions() {
    const annotations = this.getStateEvents().map(
      (curItem, curIndex) =>
        ({
          type: "line",
          label: {
            content: this.props.intl.formatMessage({
              id: `ChargerStatus_${curItem.statusCode ?? ""}`,
            }),
            enabled: false,
            // put all labels on the screen, vertically shifted
            backgroundColor: "transparent",
            color: cssContent.GRAPH_COLOR_AXIS,
            font: { weight: "normal" } as unknown as FontSpec,
            yAdjust:
              this.props.chargingStates && this.props.chargingStates.length > 1
                ? 100 * (-0.5 + curIndex / (this.props.chargingStates.length - 1))
                : 0,
          },
          xMin: curItem.timestamp * 1000,
          xMax: curItem.timestamp * 1000,
          borderColor: getColor(curItem.statusCode, true),
          borderWidth: 1,
        } as AnnotationOptions<"line">),
    );

    const options: ChartOptions<"line"> = {
      scales: {
        x: {
          type: "time",
          time: {
            minUnit: "minute", // or "hour"?
            displayFormats: {
              minute: this.props.intl.formatMessage(messages.graphFormatAxisMinute),
              hour: this.props.intl.formatMessage(messages.graphFormatAxisMinute),
              day: this.props.intl.formatMessage(messages.graphFormatAxisDay),
            },
          },
          min: this.startTimestamp ? this.startTimestamp * 1000 : undefined,
          max: this.stopTimestamp ? this.stopTimestamp * 1000 : undefined,
        },
        y: {
          title: {
            display: true,
            text: "kW",
          },
          beginAtZero: true,
        },
        yCost: {
          display: this.props.showChargerTariff,
          title: {
            text: `${this.tariffs[0]?.currency ?? "EUR"}/kWh`,
            display: true,
          },
          position: "right",
          beginAtZero: true,
          grid: {
            display: false,
          },
        },
        ySoc: {
          display: this.props.showSoc,
          title: {
            text: "%",
            display: true,
          },
          position: "right",
          min: 0,
          max: 100,
          grid: {
            display: false,
          },
        },
      },
      datasets: {
        line: {
          pointRadius: 0, // default 3
          tension: 0.4, // line smoothness, default 0
          borderColor: cssContent.GRAPH_COLOR,
          backgroundColor: hexToRgba(cssContent.GRAPH_COLOR, 0.2),
          fill: true,
          stepped: true,
          showLine: true,
        },
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      plugins: {
        legend: {
          display: this.props.showChargerTariff || this.props.showSoc,
        },
        tooltip: {
          enabled: false,
          mode: "x", // show all data items on same x value
          position: "nearest", // show tooltip on average value, not at mouse cursor
          callbacks: {
            title: (tooltipItems: TooltipItem<"line">[]) => {
              const curItem = tooltipItems[0];
              const date = new Date(curItem.parsed.x);
              const localeOptions: Intl.DateTimeFormatOptions = {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "numeric",
              };
              return date.toLocaleString(this.props.intl.locale, localeOptions);
            },
            label: (tooltipItem) => {
              const propName = tooltipItem.dataset.label ? `${tooltipItem.dataset.label}: ` : "";
              const value = roundToMinDigits(tooltipItem.parsed.y, 3);
              return ` ${propName}${value} kW`;
            },
          },
        },
        datalabels: {
          display: false,
        },
        annotation: {
          annotations,
        },
      },
    };

    return options;
  }

  public render() {
    // make some general calculations
    this.preProcessData();

    // isLoading would be sufficient, but this takes long time to be set which causes flickering
    if (
      this.values.length < 3 &&
      isSet(this.props.chargingStates) &&
      this.props.chargingStates.length === 0
    ) {
      return <></>;
    }
    return (
      <>
        <h1>
          <FormattedMessage id="Charging Progress" />
        </h1>
        <div id="charginggraph" className="graph" ref={this.compRef} key={this.oldHeight}>
          {this.props.isLoading && <div id="loading-spinner" />}
          <Line
            ref={(c) => {
              if (isSet(c)) this.chartRef = c as any;
            }}
            data={this.createGraphData()}
            options={this.createGraphOptions()}
          />
        </div>
      </>
    );
  }
}

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(ChargingSessionGraphComponent),
);
